import {verify as verifyCaptcha, getJob} from './generated/endpoints/converter/converter'
import type {ConverterVerifyResponse} from './generated/model/converterVerifyResponse'
import type {ConverterJobStatusResponse} from './generated/model/converterJobStatusResponse'

/** TSOA/orval-typed JSON call — small body, fits the normal generated-client pattern. */
export const verifyConverterCaptcha = async (tokenV3: string): Promise<ConverterVerifyResponse> => {
  const res = await verifyCaptcha({tokenV3})
  return res.data
}

/** TSOA/orval-typed poll — GET /converter/jobs/:jobId (real progress, parsed from ffmpeg's own output). */
export const pollConverterJob = async (jobId: string): Promise<ConverterJobStatusResponse> => {
  const res = await getJob(jobId)
  return res.data
}

export interface StartVideoToGifJobParams {
  videoFile: File
  startSec: number
  durationSec: number
  longEdgePx: number
  fps: number
  colors: number
  ticket: string
}

export class ConverterVideoApiError extends Error {
  readonly status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

/** Raw multipart upload — not a TSOA/orval-typed endpoint (see be/src/routes/converterVideoRoutes.ts).
 * Uses XMLHttpRequest instead of fetch specifically for `xhr.upload.onprogress` —
 * fetch has no reliable cross-browser way to report request-body upload progress,
 * and for a large video that upload can take a meaningful slice of the overall
 * wait, so leaving it unreported made the caller's progress bar sit at 0% then
 * jump once the (already-uploaded) job started reporting real ffmpeg progress.
 * Returns immediately (202) with a job id; poll pollConverterJob(jobId) for real
 * conversion progress + the result. */
export const startVideoToGifJob = (
  params: StartVideoToGifJobParams,
  onUploadProgress?: (fraction: number) => void,
): Promise<{jobId: string}> => {
  const form = new FormData()
  form.append('video', params.videoFile)
  form.append('startSec', String(params.startSec))
  form.append('durationSec', String(params.durationSec))
  form.append('longEdgePx', String(params.longEdgePx))
  form.append('fps', String(params.fps))
  form.append('colors', String(params.colors))
  form.append('ticket', params.ticket)

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', '/api/converter/video-to-gif')
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onUploadProgress?.(e.loaded / e.total)
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as {jobId: string})
        } catch {
          reject(new ConverterVideoApiError(xhr.status, 'Invalid response'))
        }
        return
      }
      let message = `HTTP ${xhr.status}`
      try {
        const body = JSON.parse(xhr.responseText) as {message?: string}
        if (body.message) message = body.message
      } catch {
        // Non-JSON error body — keep the generic HTTP status message.
      }
      reject(new ConverterVideoApiError(xhr.status, message))
    }
    xhr.onerror = () => reject(new ConverterVideoApiError(0, 'Network error'))
    xhr.send(form)
  })
}
